import { TOOLBAR_BUTTON_SX } from "@/controls/buttons/toolbarButton";
import { ResetFiltersButton } from "@/controls/buttons/ResetFiltersButton";
import { Autocomplete, Box, Button, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { ShowOnlyActivesCheckbox } from "@/controls/filters/ShowOnlyActivesCheckbox";
import type { ProductSizeFilterOption } from "../models/Recipe";

const ALL = "All";

interface RecipesToolbarProps {
	search: string;
	onSearchChange: (value: string) => void;
	productType: string | null;
	onProductTypeChange: (value: string | null) => void;
	productTypes: string[];
	productSizeId: number | null;
	onProductSizeChange: (value: number | null) => void;
	productSizes: ProductSizeFilterOption[];
	onlyActives: boolean;
	onOnlyActivesChange: (value: boolean) => void;
	onAdd: () => void;
	onReset: () => void;
	canReset: boolean;
}

export const RecipesToolbar = ({
	search,
	onSearchChange,
	productType,
	onProductTypeChange,
	productTypes,
	productSizeId,
	onProductSizeChange,
	productSizes,
	onlyActives,
	onOnlyActivesChange,
	onAdd,
	onReset,
	canReset,
}: RecipesToolbarProps) => {
	const sizeOptions = [{ Id: 0, Name: ALL }, ...productSizes];
	const selectedSize =
		sizeOptions.find((option) => option.Id === productSizeId) ?? sizeOptions[0];

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				flexWrap: "wrap",
				px: 1.5,
				py: 0.75,
			}}
		>
			<TextField
				size="small"
				placeholder="Search..."
				value={search}
				onChange={(e) => onSearchChange(e.target.value)}
				sx={{ width: 200 }}
			/>

			<Autocomplete
				size="small"
				options={[ALL, ...productTypes]}
				value={productType ?? ALL}
				disableClearable
				autoHighlight
				onChange={(_event, option) =>
					onProductTypeChange(option === ALL ? null : option)
				}
				sx={{ width: 180 }}
				renderInput={(params) => (
					<TextField
						{...params}
						label="Product Type"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				)}
			/>

			<Autocomplete
				size="small"
				options={sizeOptions}
				value={selectedSize}
				disableClearable
				autoHighlight
				getOptionLabel={(option) => option.Name}
				isOptionEqualToValue={(option, current) => option.Id === current.Id}
				onChange={(_event, option) =>
					onProductSizeChange(option.Id === 0 ? null : option.Id)
				}
				renderOption={(props, option) => {
					const { key, ...rest } = props as typeof props & { key: string };
					return (
						<li key={option.Id} {...rest}>
							{option.Name}
						</li>
					);
				}}
				sx={{ width: 230 }}
				renderInput={(params) => (
					<TextField
						{...params}
						label="Product"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				)}
			/>

			<ShowOnlyActivesCheckbox
				checked={onlyActives}
				onChange={onOnlyActivesChange}
			/>

			<ResetFiltersButton onClick={onReset} disabled={!canReset} />

			<Box sx={{ flex: 1 }} />

			<Button
				variant="contained"
				color="primary"
				startIcon={<AddIcon />}
				onClick={onAdd}
				size="medium"
				sx={TOOLBAR_BUTTON_SX}
			>
				ADD RECIPE
			</Button>
		</Box>
	);
};
